export const TRAITS = ["DEATH", "DISABILITY", "SERIOUS_ILLNESS"] as const;
export type Trait = typeof TRAITS[number];
export type Capitals = { DEATH: number } & Partial<Record<Trait, number>>;
export type NextAction = "none" | "poll" | "retry_same_intention" | "contact_support";
export type RateStatus = "ACCEPTED" | "REJECTED" | "UNDETERMINED" | "DOWNGRADED";
export type CandidateState = "prepared" | "rate_inflight" | "rate_unknown" | "rate_received"
  | "archive_inflight" | "archive_unknown" | "archived";

export interface Capabilities {
  schemaVersion: "customer-axa-capabilities-v1";
  mode: "candidates" | "legacy";
  creationAllowed: boolean;
  diagnosticCode: null | "INPUT_INCOMPLETE" | "PROFILE_UNSUPPORTED" | "CREATION_DISABLED" | "BINDING_UNAVAILABLE";
  purchaseAllowed: false;
}

export interface Candidate {
  schemaVersion: "customer-axa-candidate-v1";
  candidateId: string;
  state: CandidateState;
  rateStatus: RateStatus | null;
  quoteUlid: string;
  quoteVersion: number;
  capitalsByTrait: Capitals;
  coverage: "basic" | "premium" | "eg" | "multi";
  effectDateOriginal: string;
  price: { currency: "EUR"; yearlyPriceCents: number; monthlyPricesCents: number[] } | null;
  archive: { pricingId: string; contentHash: string; archivedAt: string } | null;
  temporalAssessment: "unknown";
  purchaseAllowed: false;
  diagnosticCode: string | null;
  nextAction: NextAction;
}

export interface Selection {
  schemaVersion: "customer-axa-selection-v1";
  selectionId: string;
  candidateId: string;
  state: "pending" | "selected";
  candidate: Candidate;
  selection: {
    quoteUlid: string; quoteVersion: number; version: number; pricingId: string;
    selectedAt: string; archiveContentHash: string;
  } | null;
  diagnosticCode: string | null;
  nextAction: NextAction;
  purchaseAllowed: false;
}

export const isUlid = (value: unknown): value is string =>
  typeof value === "string" && /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/.test(value);
const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const integer = (value: unknown): value is number => Number.isSafeInteger(value) && (value as number) >= 0;
const exact = (value: Record<string, unknown>, keys: string[]) =>
  Object.keys(value).length === keys.length && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
const date = (value: unknown): value is string => typeof value === "string"
  && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value) && Number.isFinite(Date.parse(value));
const hash = (value: unknown) => typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
const action = (value: unknown) => ["none", "poll", "retry_same_intention", "contact_support"].includes(value as string);
const diagnostic = (value: unknown) => value === null || (typeof value === "string" && /^[A-Z][A-Z0-9_]{0,79}$/.test(value));

export function isCapitals(value: unknown): value is Capitals {
  return object(value) && integer(value.DEATH) && value.DEATH > 0
    && Object.keys(value).every(key => TRAITS.includes(key as Trait) && integer(value[key]) && (value[key] as number) > 0);
}

export function parseCapabilities(value: unknown): Capabilities {
  if (!object(value) || !exact(value, ["schemaVersion", "mode", "creationAllowed", "diagnosticCode", "purchaseAllowed"])
    || value.schemaVersion !== "customer-axa-capabilities-v1" || !["candidates", "legacy"].includes(value.mode as string)
    || typeof value.creationAllowed !== "boolean" || value.purchaseAllowed !== false
    || ![null, "INPUT_INCOMPLETE", "PROFILE_UNSUPPORTED", "CREATION_DISABLED", "BINDING_UNAVAILABLE"].includes(value.diagnosticCode as any)
    || (value.mode === "legacy" && value.creationAllowed)) throw new Error("INVALID_CANDIDATE_CAPABILITIES");
  return value as unknown as Capabilities;
}

// Validate the public projection before showing it; never retain extra evidence or medical fields.
export function parseCandidate(value: unknown): Candidate {
  const fail = (): never => { throw new Error("INVALID_CANDIDATE_PROJECTION"); };
  if (!object(value) || !exact(value, ["schemaVersion", "candidateId", "state", "rateStatus", "quoteUlid",
    "quoteVersion", "capitalsByTrait", "coverage", "effectDateOriginal", "price", "archive", "temporalAssessment",
    "purchaseAllowed", "diagnosticCode", "nextAction"])) return fail();
  if (value.schemaVersion !== "customer-axa-candidate-v1" || !isUlid(value.candidateId) || !isUlid(value.quoteUlid)
    || !integer(value.quoteVersion) || value.quoteVersion < 1 || value.quoteVersion > 2147483647
    || !["prepared", "rate_inflight", "rate_unknown", "rate_received", "archive_inflight", "archive_unknown", "archived"].includes(value.state as string)
    || !(value.rateStatus === null || ["ACCEPTED", "REJECTED", "UNDETERMINED", "DOWNGRADED"].includes(value.rateStatus as string))
    || !isCapitals(value.capitalsByTrait) || !["basic", "premium", "eg", "multi"].includes(value.coverage as string)
    || typeof value.effectDateOriginal !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value.effectDateOriginal)
    || value.temporalAssessment !== "unknown" || value.purchaseAllowed !== false
    || !action(value.nextAction) || !diagnostic(value.diagnosticCode)) return fail();
  if (value.price !== null) {
    const p = value.price;
    if (value.rateStatus !== "ACCEPTED" || !object(p) || !exact(p, ["currency", "yearlyPriceCents", "monthlyPricesCents"])
      || p.currency !== "EUR" || !integer(p.yearlyPriceCents) || !Array.isArray(p.monthlyPricesCents)
      || p.monthlyPricesCents.length !== 12 || !p.monthlyPricesCents.every(integer)) return fail();
  }
  if (value.archive !== null) {
    const a = value.archive;
    if (value.state !== "archived" || !object(a) || !exact(a, ["pricingId", "contentHash", "archivedAt"])
      || !isUlid(a.pricingId) || !hash(a.contentHash) || !date(a.archivedAt)) return fail();
  } else if (value.state === "archived") return fail();
  return value as unknown as Candidate;
}

export function parseSelection(value: unknown): Selection {
  const fail = (): never => { throw new Error("INVALID_SELECTION_PROJECTION"); };
  if (!object(value) || !exact(value, ["schemaVersion", "selectionId", "candidateId", "state", "candidate", "selection",
    "diagnosticCode", "nextAction", "purchaseAllowed"]) || value.schemaVersion !== "customer-axa-selection-v1"
    || !isUlid(value.selectionId) || !isUlid(value.candidateId) || !["pending", "selected"].includes(value.state as string)
    || value.purchaseAllowed !== false || !action(value.nextAction) || !diagnostic(value.diagnosticCode)) return fail();
  const candidate = parseCandidate(value.candidate);
  if (candidate.candidateId !== value.candidateId || candidate.state !== "archived" || candidate.rateStatus !== "ACCEPTED") return fail();
  if (value.state === "pending") {
    if (value.selection !== null) return fail();
  } else {
    const s = value.selection;
    if (!object(s) || !exact(s, ["quoteUlid", "quoteVersion", "version", "pricingId", "selectedAt", "archiveContentHash"])
      || s.quoteUlid !== candidate.quoteUlid || s.quoteVersion !== candidate.quoteVersion
      || !integer(s.version) || s.version < 2 || s.version > 2147483647
      || s.pricingId !== candidate.archive?.pricingId || s.archiveContentHash !== candidate.archive?.contentHash
      || !date(s.selectedAt)) return fail();
  }
  return value as unknown as Selection;
}
